import React, { useRef, useState, useEffect, useCallback } from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';
import '../../styles/cropper.css'

interface ImageCropModalProps {
  imageSrc: string;
  open: boolean;
  onClose: () => void;
  onCropComplete: (croppedImage: string) => void;
  /** Proporção fixa do recorte (ex.: 1 = quadrado). Padrão: livre. */
  aspectRatio?: number;
  /** Lado máximo da imagem gerada, em px. Padrão: 4096. */
  maxOutputSize?: number;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({ imageSrc, open, onClose, onCropComplete, aspectRatio = NaN, maxOutputSize = 4096 }) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const [loading, setLoading] = useState(false);
  const [cropperReady, setCropperReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  // Garantir que o componente está montado antes de renderizar
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Reset states quando o modal abre/fecha
  useEffect(() => {
    if (open) {
      setCropperReady(false);
      setError(null);
      setLoading(false);
    }
  }, [open]);

  // Handler quando o cropper está pronto
  const handleCropperReady = useCallback(() => {
    setCropperReady(true);
    setError(null);
  }, []);


  const handleSave = useCallback(() => {
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      const cropper = cropperRef.current?.cropper;

      if (!cropper) {
        setError('Erro ao processar imagem. Por favor, tente novamente.');
        setLoading(false);
        return;
      }

      const canvas = cropper.getCroppedCanvas({
        maxWidth: maxOutputSize,
        maxHeight: maxOutputSize,
        fillColor: '#fff',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      if (!canvas) {
        setError('Erro ao gerar imagem recortada. Por favor, tente novamente.');
        setLoading(false);
        return;
      }

      // Exportar como PNG para preservar transparência (evita fundo preto em imagens PNG)
      const croppedDataUrl = canvas.toDataURL('image/png', 1.0);

      if (!croppedDataUrl || croppedDataUrl === 'data:,') {
        setError('Erro ao exportar imagem. Por favor, tente novamente.');
        setLoading(false);
        return;
      }

      onCropComplete(croppedDataUrl);
    } catch (err) {
      console.error('Erro ao salvar recorte:', err);
      setError('Erro inesperado ao processar imagem. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [loading, onCropComplete]);

  const handleClose = useCallback(() => {
    if (!loading) {
      onClose();
    }
  }, [loading, onClose]);

  // Não renderizar se não estiver aberto ou não montado
  if (!open || !isMounted) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 p-4"
      style={{ zIndex: 99999 }}
      onClick={(e) => {
        // Fechar ao clicar fora do modal
        if (e.target === e.currentTarget && !loading) {
          handleClose();
        }
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        style={{ overflow: 'visible' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recortar & Gerenciar</h2>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 sm:p-6 overflow-hidden">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <div
            className="relative w-full bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden"
            style={{ height: '350px', minHeight: '250px' }}
          >
            {!cropperReady && !error && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-900">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Carregando imagem...</span>
                </div>
              </div>
            )}

            <Cropper
              src={imageSrc}
            style={{ height: 350, width: '100%' }}
              initialAspectRatio={aspectRatio}
              aspectRatio={aspectRatio}
              guides={true}
              viewMode={1}
              dragMode="crop"
              background={false}
              responsive={true}
              autoCropArea={1}
              checkOrientation={false}
              ref={cropperRef}
              ready={handleCropperReady}
              cropend={() => { }} // Evento para garantir atualização do canvas
              zoomable={true}
              scalable={true}
              movable={true}
              rotatable={false}
            />
          </div>
        </div>

        {/* Footer com botões - SEMPRE VISÍVEL */}
        <div
          className="flex-shrink-0 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-lg"
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            flexWrap: 'wrap'
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2.5 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ minWidth: '100px' }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || !cropperReady || !!error}
            className="px-4 py-2.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ minWidth: '120px' }}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Salvando...</span>
              </>
            ) : (
              'Salvar corte'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
